import React, { useState } from "react";
import PropTypes from "prop-types";
import { Paper, IconButton, Box } from "@mui/material";
import {
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
} from "@mui/icons-material";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

function PdfViewer({ file, title }) {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPageNumber(1);
  };

  const changePage = (offset) => {
    setPageNumber((prevPageNumber) => prevPageNumber + offset);
  };

  const zoomIn = () => {
    setScale((prevScale) => Math.min(2.0, prevScale + 0.1));
  };

  const zoomOut = () => {
    setScale((prevScale) => Math.max(0.5, prevScale - 0.1));
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        backgroundColor: "rgba(0, 0, 0, 0.02)",
        borderRadius: 1,
      }}
    >
      <MDBox
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          height: "calc(100vh - 300px)",
          "& .react-pdf__Document": {
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
          },
          "& .react-pdf__Page": {
            maxWidth: "100%",
            marginBottom: 2,
            "& canvas": {
              maxWidth: "100%",
              height: "auto !important",
            },
          },
        }}
      >
        <Box sx={{ width: "100%", mb: 2 }}>
          <MDBox display="flex" justifyContent="space-between" alignItems="center">
            <MDTypography variant="h6" fontWeight="medium">
              {title || "Document Preview"}
            </MDTypography>
            <MDBox display="flex" alignItems="center" gap={1}>
              <IconButton onClick={zoomOut} size="small" title="Zoom out">
                <ZoomOutIcon fontSize="small" />
              </IconButton>
              <IconButton onClick={zoomIn} size="small" title="Zoom in">
                <ZoomInIcon fontSize="small" />
              </IconButton>
              <IconButton
                onClick={() => changePage(-1)}
                disabled={pageNumber <= 1}
                size="small"
                title="Previous page"
              >
                <PrevIcon fontSize="small" />
              </IconButton>
              <MDTypography variant="button">
                {pageNumber} / {numPages || "-"}
              </MDTypography>
              <IconButton
                onClick={() => changePage(1)}
                disabled={pageNumber >= numPages}
                size="small"
                title="Next page"
              >
                <NextIcon fontSize="small" />
              </IconButton>
            </MDBox>
          </MDBox>
        </Box>
        <Document
          file={file}
          onLoadSuccess={onDocumentLoadSuccess}
          loading={<MDTypography>Loading PDF...</MDTypography>}
          error={<MDTypography color="error">Failed to load PDF file.</MDTypography>}
        >
          <Page
            pageNumber={pageNumber}
            scale={scale}
            renderTextLayer
            renderAnnotationLayer
            loading={<MDTypography>Loading page...</MDTypography>}
            error={<MDTypography color="error">Failed to load page.</MDTypography>}
          />
        </Document>
      </MDBox>
    </Paper>
  );
}

PdfViewer.propTypes = {
  file: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(File)]).isRequired,
  title: PropTypes.string,
};

PdfViewer.defaultProps = {
  title: undefined,
};

export default PdfViewer;
